import { useEffect, useState } from 'react';
import {
  type DefaultValues,
  type FieldValues,
  type Path,
  useForm,
  type UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useResourceRecord, useSaveResource } from '@/lib/api/resource';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import { useUnsavedChanges } from '@/lib/forms/useUnsavedChanges';

/**
 * The plumbing every content editor in §4 repeats, once.
 *
 * Fetch the record, fill the form when it lands, guard the unsaved changes,
 * submit, place the server's field errors, and go to the record's own address
 * after a create. None of that differs between modules — what differs is the
 * fields, which stay in each editor's JSX where they can be read.
 *
 * A hook rather than a component for exactly that reason: a `<ResourceEditor>`
 * taking a render prop would put every module's fields inside a callback and
 * buy nothing, while this leaves the markup alone.
 *
 * Two things are deliberately *not* here. `toFields`/`toInput` are per module,
 * because the mapping between a form and a payload is where the interesting
 * decisions live — an emptied number that must be `null` rather than `NaN`, a
 * status a role may not set. And the publishing sidebar is JSX, so it belongs
 * with the rest of the JSX.
 */
export interface ResourceFormOptions<TRow, TFields extends FieldValues, TInput> {
  /** The `/admin/{resource}` segment. */
  resource: string;
  /** The record id, or `new`. From the module's splat route. */
  recordId: string;
  /** A blank record, for the create case. */
  empty: () => TFields;
  toFields: (row: TRow) => TFields;
  toInput: (fields: TFields) => TInput;
  /** Field names `applyApiErrors` may place a server message on. */
  errorFields: string[];
  /** Where a create lands. Defaults to `/{resource}/{id}`. */
  pathFor?: (row: TRow & { id: number }) => string;
}

export interface ResourceForm<TRow, TFields extends FieldValues> {
  form: UseFormReturn<TFields>;
  /** `watch()`, so a field component can be given a value rather than a ref. */
  values: TFields;
  /** The loaded record — absent while it is in flight, and for a create. */
  record: TRow | undefined;
  isNew: boolean;
  /** True only while an existing record is being fetched. */
  isLoading: boolean;
  isSubmitting: boolean;
  /** A message that belongs to the form rather than to any one field. */
  formError: string | null;
  submit: (event?: React.BaseSyntheticEvent) => Promise<void>;
  /** §1's router-half unsaved-changes guard. */
  guard: ReturnType<typeof useUnsavedChanges>;
}

export function useResourceForm<TRow extends { id: number }, TFields extends FieldValues, TInput>({
  resource,
  recordId,
  empty,
  toFields,
  toInput,
  errorFields,
  pathFor,
}: ResourceFormOptions<TRow, TFields, TInput>): ResourceForm<TRow, TFields> {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isNew = recordId === 'new';

  const [formError, setFormError] = useState<string | null>(null);

  const record = useResourceRecord<TRow>(resource, isNew ? null : Number(recordId));
  const save = useSaveResource<TRow, TInput>(resource);

  const form = useForm<TFields>({ defaultValues: empty() as DefaultValues<TFields> });

  const { reset, handleSubmit, setError, watch, formState } = form;

  /*
   * Filled rather than initialised: the record arrives after the first render.
   * `reset` also clears the dirty flag, which the guard below depends on — a
   * form that counted the server's own values as changes would block every
   * navigation away from a record nobody touched.
   */
  useEffect(() => {
    if (record.data) {
      reset(toFields(record.data.data));
    }
    // `toFields` is redefined per render in most modules; depending on it would
    // reset the form on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.data, reset]);

  const guard = useUnsavedChanges(formState.isDirty && !formState.isSubmitting);

  const submit = handleSubmit(async (fields) => {
    setFormError(null);

    try {
      const saved = await save.mutateAsync({
        id: isNew ? undefined : Number(recordId),
        input: toInput(fields),
      });

      /*
       * Straight to the saved record, so a create lands on its own address
       * rather than leaving the form thinking it is still new — the next save
       * would otherwise POST a second copy.
       */
      navigate(pathFor ? pathFor(saved.data) : `/${resource}/${saved.data.id}`, { replace: true });
      reset(toFields(saved.data));
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(
        error,
        setError as never,
        errorFields as Path<TFields>[] as never,
      );

      // A message with nowhere to go still has to be shown: a 409, a 500, or a
      // 422 about a field this screen does not render.
      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return {
    form,
    values: watch(),
    record: record.data?.data,
    isNew,
    isLoading: !isNew && record.isPending,
    isSubmitting: formState.isSubmitting,
    formError,
    submit,
    guard,
  };
}
